
import { LineItemStatusSummary } from "@/models/OrderLineItemSummary";
import ExpandTransition from "@/components/ExpandTransition";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import DateFormatting from "@/mixins/DateFormatting";
import { SEARCH_VENDORS, VENDOR_BY_ID } from "@/gql/vendors";

type PoSize = {
	sizeId: number | null;
	name: string;
	lengthId: number | null;
	length: string;
	ordered: number;
	received: number;
	backOrdered: number;
	dropShipRecords: DropShipRecord[] | null;
};

type DropShipRecord = {
	id: number;
	lineItemId: number;
	itemId: number | null;
	styleHash: string | null;
	purchaseOrderId: number;
	sizeId: number | null;
	name: string | null;
	sizeName: string | null;
	lengthId: number | null;
	length: string | null;
	ordered: number;
	shipped: number;
	showDatePicker: boolean;
	shipDate: string | null;
	formattedShipDate: string | null;
	trackingNumber: string | null;
};

type PoLineItem = {
	lineItemId: number;
	lineItemNumber: number;
	inventoryItemId: number;
	systemCategoryId: number;
	styleCode: string;
	styleHash: string;
	description: string;
	ordered: number;
	received: number;
	backOrdered: number;
	sizes: PoSize[];
};

type PurchaseOrder = {
	purchaseOrderId: number;
	purchaseOrderNumber: string;
	vendorId: number;
	vendor: string;
	ordered: number;
	received: number;
	orderType: string;
	backOrdered: number;
	salesOrderId: number | null;
	purchaseOrderDate: string | null;
	orderStatus: string;
	purchaseOrderPrinted: string | null;
	packingSlipPrinted: string | null;
};

type PoDetailed = {
	purchaseOrder: PurchaseOrder;
	lineItems: PoLineItem[];
};

type PoResponse = {
	data: PoDetailed[];
};

type Address = {
	vendorAddressId: number;
	label: string;
	addressLine1: string;
	addressLine2: string;
	addressTypeId: number | null;
	city: string;
	state: string;
	postalCode: string;
	isPrimaryAddress: boolean;
};

type Contact = {
	vendorContactId: number;
	primaryContact: boolean;
	firstName: string;
	lastName: string;
	workPhoneNumber: string | null;
	cellPhoneNumber: string;
	email: string;
	isActive: boolean;
};

type Vendor = {
	vendorId: number;
	companyName: string;
	addresses: Address[];
	contacts: Contact[];
	vendorContactId: number | null;
};

export default {
	name: "DropshipOrdersStatus",
	mixins: [DateFormatting],
	components: {
		ExpandTransition,
		SystemCategoryIcon,
	},
	props: {
		salesOrderId: {
			type: Number,
			required: true,
		},
		lineItems: {
			type: Array as () => LineItemStatusSummary[],
			required: false,
		},
		posToUpdate: {
			type: Array as () => PoDetailed[],
			required: false,
		},
		hardRefresh: {
			type: Number,
			required: false,
		},
		canPrintVendorPOs: {
			type: Boolean,
			default: false,
		},
	},
	data(): {
		showDateByRow: number | null;
		showItemTableByRow: string | null;
		showSubTableByRow: string | null;
		showTable: string[];
		dropshipOrders: PoDetailed[];
		today: string;
		hoveredSizeIndex: number | null;
		editVendor: PurchaseOrder | null;
		editPoDate: PurchaseOrder | null;
		vendorSearch: Vendor | null;
		vendorResults: Vendor[];
		contacts: Contact[];
		addresses: Address[];
		showOrderDate: boolean;
		showOnlyOnOrder: boolean;
	} {
		return {
			showDateByRow: null,
			showItemTableByRow: null,
			showSubTableByRow: null,
			showTable: [""],
			dropshipOrders: [],
			today: new Date().toISOString().slice(0, 10),
			hoveredSizeIndex: null,
			editVendor: null,
			editPoDate: null,
			vendorSearch: null,
			vendorResults: [],
			contacts: [],
			addresses: [],
			showOrderDate: false,
			showOnlyOnOrder: false,
		};
	},
	methods: {
		async getDropshipOrders(salesOrderId: number): Promise<PoResponse> {
			try {
				const response = await this.$http.get(`/api/orders/${salesOrderId}/purchase-orders`);
				if (response.data && Array.isArray(response.data.data)) {
					const filteredData = response.data.data.filter((po: any) => po.purchaseOrder.dropShip);

					// Update dropShipRecords for each size
					filteredData.forEach((order: PoDetailed) => {
						order.lineItems.forEach((item: PoLineItem) => {
							item.sizes.forEach((size: PoSize) => {
								if (!size.dropShipRecords || size.dropShipRecords.length === 0) {
									size.dropShipRecords = [
										{
											id: 0,
											lineItemId: item.lineItemId,
											itemId: item.inventoryItemId,
											styleHash: item.styleHash,
											purchaseOrderId: order.purchaseOrder.purchaseOrderId,
											sizeId: size.sizeId,
											name: size.name,
											sizeName: size.name,
											lengthId: size.lengthId,
											length: size.length,
											ordered: size.ordered,
											shipped: 0,
											showDatePicker: false,
											shipDate: null,
											formattedShipDate: null,
											trackingNumber: null,
										},
									];
								} else {
									// ensure meta data is applied
									size.dropShipRecords.forEach((dsr: DropShipRecord) => {
										dsr.lineItemId = item.lineItemId;
										dsr.itemId = item.inventoryItemId;
										dsr.styleHash = item.styleHash;
										dsr.purchaseOrderId = order.purchaseOrder.purchaseOrderId;
										dsr.sizeId = size.sizeId;
										dsr.sizeName = size.name;
										dsr.name = size.name;
										dsr.lengthId = size.lengthId;
										dsr.length = size.length;
										dsr.ordered = size.ordered;
										dsr.showDatePicker = false;
										dsr.formattedShipDate = null;
									});
								}
							});
						});
					});

					return { data: filteredData };
				} else {
					console.warn("Received data may not be an array", response.data);
					return { data: [] };
				}
			} catch (error) {
				console.error("Error fetching dropship orders:", error);
				return { data: [] };
			}
		},
		poLineNumberMismatch(poNumber: string, lineItems: PoLineItem[]): number | null {
			if (!poNumber || !lineItems?.length) return null;
			const parts = poNumber.split('-');
			if (parts.length < 2) return null;
			const embeddedLineNum = parseInt(parts[1]);
			if (isNaN(embeddedLineNum)) return null;
			const currentLineNum = lineItems[0]?.lineItemNumber;
			if (!currentLineNum || embeddedLineNum === currentLineNum) return null;
			return currentLineNum;
		},
		renderOrderStatus(orderStatus: string) {
			let status = {
				label: "",
				color: "",
			};
			switch (orderStatus) {
				case "OnOrder":
					status.label = "On Order";
					status.color = "table-status-gray";
					break;
				case "PartiallyShipped":
					status.label = "Partially Shipped";
					status.color = "table-status-gray";
					break;
				case "Shipped":
					status.label = "Shipped";
					status.color = "table-status-green";
					break;
				case "Cancelled":
					status.label = "Cancelled";
					status.color = "table-status-red";
					break;
				default:
					status.label = "Error";
					status.color = "table-status-orange";
			}

			return status;
		},
		async searchVendors(input: any) {
			try {
				const res = await this.$apollo.query({
					fetchPolicy: "no-cache",
					query: SEARCH_VENDORS,
					variables: {
						searchTerm: `${input}`,
					},
				});
				this.vendorResults = res.data.response.items;
			} catch (error) {
				console.log(error);
			}
		},
		async getAndSetVendorById(vendorId: number) {
			try {
				const res = await this.$apollo.query({
					fetchPolicy: "no-cache",
					query: VENDOR_BY_ID,
					variables: {
						id: vendorId,
					},
				});

				this.vendorSearch = res.data.response;

				//set the assigned vendor data
				this.setVendor();
			} catch (error) {
				console.log(error);
			}
		},
		setVendor() {
			console.log(this.vendorSearch);

			if (this.vendorSearch) {
				//assign the selected vendor contacts and addresses
				this.contacts = this.vendorSearch.contacts || [];
				this.addresses = this.vendorSearch.addresses || [];

				//assign the selected vendor data to poData
				if (this.contacts.some((obj) => obj.primaryContact == true)) {
					//assign the first found primary contact
					this.vendorSearch.vendorContactId =
						this.contacts.find((obj) => obj.primaryContact == true)?.vendorContactId ?? null;
				} else {
					//assign a default first contact
					this.vendorSearch.vendorContactId = this.contacts[0]?.vendorContactId;
				}
			}
		},
		async saveVendor(po: PurchaseOrder) {
			try {
				const res = await this.$http.post(
					`/api/purchasing/orders/${po.purchaseOrderId}/edit-vendor`,
					{
						vendorId: this.vendorSearch?.vendorId,
						vendorContactId: this.vendorSearch?.vendorContactId,
					}
				);
				console.log(res);
				this.init();
			} catch (error) {
				console.log(error);
			}
		},
		async saveOrderDate(po: PurchaseOrder) {
			try {
				const res = await this.$http.post(
					`/api/purchasing/orders/${po.purchaseOrderId}/edit-date`,
					{
						orderDate: po.purchaseOrderDate,
					}
				);
				console.log(res);
				this.init();
			} catch (error) {
				console.log(error);
			}
		},
		getMaxShippedQuantity(size: PoSize, dropShipRecord: DropShipRecord, index: number) {
			if (index === 0 && size.dropShipRecords?.length === 1) {
				return size.ordered;
			}

			const totalShipped =
				size.dropShipRecords?.reduce((sum, record) => {
					if (record !== dropShipRecord) {
						return sum + Number(record.shipped);
					}
					return sum;
				}, 0) || 0;

			return size.ordered - totalShipped;
		},
		shouldRenderAddShipmentRecord(size: PoSize) {
			if (size.ordered === 1) {
				return false;
			}

			const totalShipped =
				size.dropShipRecords?.reduce((sum, record) => sum + Number(record.shipped), 0) || 0;
			return totalShipped < size.ordered;
		},
		handleShippedQuantity(dropShipRecord: DropShipRecord, size: PoSize, input: any) {
			let inputValue = input === "" ? 0 : parseInt(input);

			// Calculate the total shipped quantity for the size excluding the current dropShipRecord
			const totalShipped =
				size.dropShipRecords?.reduce((sum, record) => {
					if (record !== dropShipRecord) {
						// exclude the calculation of the current record
						return Number(sum) + Number(record.shipped);
					}
					return sum;
				}, 0) || 0;

			let fixedInputValue;

			if (Number(totalShipped) + Number(inputValue) > Number(size.ordered)) {
				// If the total shipped quantity exceeds the ordered quantity, set the input value to the maximum allowed
				fixedInputValue = Number(size.ordered) - Number(totalShipped);
			} else {
				// Allow single input of 0 but strip leading 0 if input is a 2-digit number or more
				fixedInputValue =
					input.length > 1
						? input.replace(/^0+/, "").replace(/[^\d.]/gi, "")
						: input.replace(/[^\d.]/gi, "");
			}

			// Update the shipped quantity of the dropShipRecord
			dropShipRecord.shipped = fixedInputValue === "" ? 0 : parseInt(fixedInputValue);
		},
		shipAllSizes(item: PoLineItem, po: PurchaseOrder) {
			item.sizes.forEach((size: PoSize) => {
				const defaultDate = this.parseDate(this.formatDate(this.today));
				const totalShipped =
					size.dropShipRecords?.reduce((sum, record) => Number(sum) + Number(record.shipped), 0) ||
					0;
				const remainingQuantity = Number(size.ordered) - Number(totalShipped);

				// Set default date if not already present
				size.dropShipRecords?.forEach((dsr: DropShipRecord) => {
					if (!dsr.shipDate || dsr.shipDate == "") {
						dsr.shipDate = defaultDate;
						dsr.formattedShipDate = defaultDate;
					}

					dsr.sizeName = dsr.name;
				});

				if (remainingQuantity > 0) {
					const lastRecord = size.dropShipRecords?.[size.dropShipRecords.length - 1];

					if (lastRecord && Number(lastRecord.shipped) === 0) {
						// Use the last record if it exists and has 0 shipped quantity
						lastRecord.shipped = remainingQuantity;

						if (!lastRecord.shipDate) {
							lastRecord.shipDate = defaultDate;
							lastRecord.formattedShipDate = defaultDate;
						}
					} else {
						// Add a new dropShipRecord
						size.dropShipRecords?.push({
							id: 0,
							lineItemId: item.lineItemId,
							itemId: item.inventoryItemId,
							styleHash: item.styleHash,
							purchaseOrderId: po.purchaseOrderId,
							sizeId: size.sizeId,
							name: size.name,
							sizeName: size.name,
							lengthId: size.lengthId,
							length: size.length,
							ordered: size.ordered,
							shipped: remainingQuantity,
							showDatePicker: false,
							shipDate: defaultDate,
							formattedShipDate: defaultDate,
							trackingNumber: null,
						});
					}
				}
			});
		},
		async ship(item: PoLineItem) {
			const dropShipRecords = item.sizes.flatMap((size: PoSize) => size.dropShipRecords || []);
			const res = await this.$http.post(
				`/api/purchasing/orders/${this.salesOrderId}/ship-dropship`,
				{
					dropShipRecords,
				}
			);

			if (res.status === 200) {
				await this.init();
				this.showSubTableByRow = null;
			}
		},
		async init() {
			const dropshipOrders = await this.getDropshipOrders(this.salesOrderId);
			this.dropshipOrders = dropshipOrders.data;

			if (this.dropshipOrders.length > 0) {
				await this.getAndSetVendorById(this.dropshipOrders[0].purchaseOrder.vendorId);
			}
		},
	},
	computed: {
		displayedOrders() {
			if (this.showOnlyOnOrder) {
				return this.dropshipOrders.filter((order) => order.purchaseOrder.orderStatus === "OnOrder");
			}
			return this.dropshipOrders;
		},
	},
	watch: {
		hardRefresh() {
			this.init();
		},
	},
	async mounted() {
		await this.init();
	},
};
